import { useEffect, useRef, useState } from 'react'
import {
  Alert, Button, Checkbox, Col, DatePicker, Image, Input, Modal, Popconfirm,
  Row, Select, Spin, Table, Tag, Tabs, Typography, message, theme,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  ArrowLeftOutlined, CameraOutlined, CarOutlined, CheckCircleOutlined,
  DownloadOutlined, MailOutlined, UserOutlined, IdcardOutlined,
  EnvironmentOutlined, HistoryOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import axiosInstance from '@/lib/axios'
import { usePageTitle } from '@/contexts/PageTitleContext'
import { useAuthenticatedImage } from '@/hooks/useAuthenticatedImage'
import { SignatureCanvas } from '@/components/SignatureCanvas'
import { CameraModal } from '@/components/CameraModal'

const { Text } = Typography
const { TextArea } = Input

// ─── Tipi ─────────────────────────────────────────────────────────────────────

interface CustomerInfo {
  id: number; tipo: string
  nome?: string; cognome?: string; ragione_sociale?: string
  email?: string; telefono?: string; cellulare?: string
  codice_fiscale?: string; luogo_nascita?: string; data_nascita?: string
  indirizzo?: string; numero_civico?: string; citta?: string; cap?: string; provincia?: string
}
interface VehicleInfo { id: number; targa: string; marca?: string; modello?: string; anno?: number }
interface AssignmentDetail {
  id: number; stato: string
  data_inizio: string; data_fine_prevista: string
  data_fine_effettiva?: string; data_consegna_effettiva?: string; data_riconsegna_effettiva?: string
  km_inizio?: number; km_fine?: number; note?: string
  numero_patente?: string; categoria_patente?: string; ente_rilascio_patente?: string; scadenza_patente?: string
  motivo_richiesta?: string; livello_carburante_inizio?: string; accessori_inclusi?: string
  danni_preesistenti?: string; firma_cliente_b64?: string; consenso_gdpr: boolean
  foto_patente_fronte_url?: string; foto_patente_retro_url?: string
  foto_danni_consegna?: Array<{ filename: string; url: string }>
  livello_carburante_fine?: string; dichiarazione_no_incidenti?: boolean
  note_incidenti?: string; firma_responsabile_b64?: string
  foto_danni_riconsegna?: Array<{ filename: string; url: string }>
  customer?: CustomerInfo; vehicle?: VehicleInfo
  courtesy_car_id: number; work_order_id?: number
}
interface AssignmentListItem {
  id: number; stato: string
  data_inizio: string; data_fine_prevista: string; data_consegna_effettiva?: string
  customer?: CustomerInfo; vehicle?: VehicleInfo
}
interface AssignmentStorico {
  id: number; stato: string
  data_inizio: string; data_fine_prevista: string
  data_consegna_effettiva?: string; data_riconsegna_effettiva?: string
  customer?: CustomerInfo; vehicle?: VehicleInfo
}

// ─── Costanti ─────────────────────────────────────────────────────────────────

const FUEL_LEVELS = [
  { value: 'vuoto', label: 'Vuoto', pct: 0 },
  { value: '1/4',   label: '¼',    pct: 25 },
  { value: '1/2',   label: '½',    pct: 50 },
  { value: '3/4',   label: '¾',    pct: 75 },
  { value: 'pieno', label: 'Pieno', pct: 100 },
]
const MOTIVI = [
  { value: 'esigenza_lavorativa', label: 'Esigenza lavorativa' },
  { value: 'necessita_familiare', label: 'Necessità familiare' },
  { value: 'altro',               label: 'Altro' },
]
const BASE_URL = '/courtesy-cars/assignments'

// ─── Componenti ausiliari ──────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  const { token } = theme.useToken()
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      marginBottom: 10, marginTop: 4,
    }}>
      <div style={{ width: 3, height: 16, background: token.colorPrimary, borderRadius: 2, flexShrink: 0 }} />
      <Text style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: token.colorTextSecondary }}>
        {children}
      </Text>
    </div>
  )
}

function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  const { token } = theme.useToken()
  return (
    <div style={{ fontSize: 12, color: token.colorTextSecondary, marginBottom: 3, lineHeight: 1.3 }}>
      {children}{required && <span style={{ color: token.colorError, marginLeft: 2 }}>*</span>}
    </div>
  )
}

function FuelPicker({ value, onChange, disabled }: { value?: string; onChange?: (v: string) => void; disabled?: boolean }) {
  const { token } = theme.useToken()
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {FUEL_LEVELS.map(l => {
        const sel = value === l.value
        return (
          <button
            key={l.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange?.(l.value)}
            style={{
              flex: 1, minHeight: 44, border: `1.5px solid ${sel ? token.colorPrimary : token.colorBorder}`,
              borderRadius: token.borderRadiusSM, background: sel ? token.colorPrimaryBg : token.colorBgContainer,
              color: sel ? token.colorPrimary : token.colorText,
              fontWeight: sel ? 700 : 400, cursor: disabled ? 'default' : 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 1, padding: '4px 2px', fontSize: 12, transition: 'all 0.15s',
              opacity: disabled ? 0.6 : 1,
            }}
          >
            <span style={{ fontSize: 10, opacity: 0.65 }}>{l.pct}%</span>
            <span>{l.label}</span>
          </button>
        )
      })}
    </div>
  )
}

// Sub-componente per un singolo item nella griglia danni.
// Componente separato per rispettare la regola hooks-in-loop: ogni item
// chiama useAuthenticatedImage una volta, al livello del proprio componente.
function PhotoGridItem({ filename, url, onDelete, disabled }: {
  filename: string; url: string; onDelete: (fn: string) => void; disabled?: boolean
}) {
  const { src } = useAuthenticatedImage(url)
  const { token } = theme.useToken()
  return (
    <div style={{ position: 'relative' }}>
      {src ? (
        <Image
          src={src}
          width={80}
          height={60}
          alt={filename}
          style={{ objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <div style={{
          width: 80, height: 60,
          borderRadius: token.borderRadiusSM,
          border: `1px solid ${token.colorBorder}`,
          background: '#f0f0f0',
        }} />
      )}
      {!disabled && (
        <button onClick={() => onDelete(filename)} type="button" style={{
          position: 'absolute', top: -5, right: -5, width: 18, height: 18,
          background: token.colorError, color: '#fff', border: 'none',
          borderRadius: '50%', cursor: 'pointer', fontSize: 11, lineHeight: '18px', padding: 0,
          zIndex: 10,
        }}>×</button>
      )}
    </div>
  )
}

function PhotoSlot({
  url, onUpload, onDelete, disabled, label,
}: {
  url?: string; onUpload: (f: File) => void; onDelete?: () => void; disabled?: boolean; label: string
}) {
  const { token } = theme.useToken()
  const [cameraOpen, setCameraOpen] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  // Hook chiamato a livello del componente: se url è undefined il hook è no-op.
  const { src } = useAuthenticatedImage(url)

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (f) onUpload(f)
    e.target.value = ''
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
      {/* Wrapper relativo: consente di posizionare il bottone × in sovrimpressione */}
      <div style={{ position: 'relative' }}>
        <div style={{
          width: 86, height: 60, borderRadius: token.borderRadiusSM,
          border: `1.5px ${url ? 'solid' : 'dashed'} ${url ? token.colorBorder : token.colorPrimaryBorder}`,
          overflow: 'hidden', background: token.colorBgContainer,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {src ? (
            // Foto caricata: mostra con Image per abilitare il visualizzatore a schermo intero.
            // Il modale preview è portato su document.body: overflow:hidden del div non lo influenza.
            <Image
              src={src}
              width={86}
              height={60}
              alt={label}
              style={{ objectFit: 'cover', display: 'block' }}
            />
          ) : url ? (
            // URL presente ma blob non ancora pronto (caricamento in corso)
            <div style={{ width: '100%', height: '100%', background: '#f0f0f0' }} />
          ) : (
            // Nessuna foto: placeholder invito all'upload
            <div style={{ textAlign: 'center', color: token.colorTextPlaceholder }}>
              <CameraOutlined style={{ fontSize: 16, display: 'block', margin: '0 auto 2px' }} />
              <span style={{ fontSize: 9 }}>Foto</span>
            </div>
          )}
        </div>
        {/* Bottone eliminazione: visibile solo quando c'è foto e slot non è in sola lettura */}
        {onDelete && url && !disabled && (
          <Popconfirm title="Eliminare la foto?" onConfirm={onDelete} okText="Sì" cancelText="No">
            <button type="button" style={{
              position: 'absolute', top: -5, right: -5, width: 18, height: 18,
              background: token.colorError, color: '#fff', border: 'none',
              borderRadius: '50%', cursor: 'pointer', fontSize: 11,
              lineHeight: '18px', padding: 0, zIndex: 10,
            }}>×</button>
          </Popconfirm>
        )}
      </div>
      <Text style={{ fontSize: 11, color: token.colorTextSecondary }}>{label}</Text>
      {!disabled && !url && (
        <div style={{ display: 'flex', gap: 4 }}>
          <button type="button" onClick={() => setCameraOpen(true)} style={{
            fontSize: 11, padding: '2px 6px', cursor: 'pointer',
            border: `1px solid ${token.colorBorder}`, borderRadius: token.borderRadiusSM,
            background: token.colorBgContainer, color: token.colorText,
          }}>📷 Camera</button>
          <button type="button" onClick={() => fileRef.current?.click()} style={{
            fontSize: 11, padding: '2px 6px', cursor: 'pointer',
            border: `1px solid ${token.colorBorder}`, borderRadius: token.borderRadiusSM,
            background: token.colorBgContainer, color: token.colorText,
          }}>📁 File</button>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
        </div>
      )}
      <CameraModal
        open={cameraOpen}
        title={`Foto ${label}`}
        onCapture={f => { onUpload(f); setCameraOpen(false) }}
        onCancel={() => setCameraOpen(false)}
      />
    </div>
  )
}

function PhotoGrid({
  items, onAdd, onDelete, disabled,
}: { items: Array<{ filename: string; url: string }>; onAdd: (f: File) => void; onDelete: (fn: string) => void; disabled?: boolean }) {
  const { token } = theme.useToken()
  const [cameraOpen, setCameraOpen] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (f) onAdd(f)
    e.target.value = ''
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'flex-start' }}>
      {/* Image.PreviewGroup abilita la navigazione tra tutte le foto della griglia
          nel visualizzatore a schermo intero. */}
      <Image.PreviewGroup>
        {items.map(({ filename, url }) => (
          <PhotoGridItem
            key={filename}
            filename={filename}
            url={url}
            onDelete={onDelete}
            disabled={disabled}
          />
        ))}
      </Image.PreviewGroup>
      {!disabled && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <button onClick={() => setCameraOpen(true)} type="button" style={{
            width: 80, height: 28, border: `1.5px dashed ${token.colorBorder}`,
            borderRadius: token.borderRadiusSM, background: token.colorBgContainer,
            cursor: 'pointer', color: token.colorTextSecondary, fontSize: 11,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
          }}>
            <CameraOutlined style={{ fontSize: 13 }} /> Camera
          </button>
          <button onClick={() => fileRef.current?.click()} type="button" style={{
            width: 80, height: 28, border: `1.5px dashed ${token.colorBorder}`,
            borderRadius: token.borderRadiusSM, background: token.colorBgContainer,
            cursor: 'pointer', color: token.colorTextSecondary, fontSize: 11,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
          }}>
            📁 File
          </button>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
        </div>
      )}
      <CameraModal
        open={cameraOpen}
        title="Scatta foto danno"
        onCapture={f => { onAdd(f); setCameraOpen(false) }}
        onCancel={() => setCameraOpen(false)}
      />
    </div>
  )
}

function CustomerBar({ customer, vehicle }: { customer: CustomerInfo; vehicle?: VehicleInfo }) {
  const { token } = theme.useToken()
  const nome = customer.tipo === 'azienda'
    ? customer.ragione_sociale
    : `${customer.nome ?? ''} ${customer.cognome ?? ''}`.trim()

  return (
    <div style={{
      display: 'flex', gap: 12, flexWrap: 'wrap',
      background: token.colorBgElevated, border: `1px solid ${token.colorBorderSecondary}`,
      borderRadius: token.borderRadius, padding: '10px 14px', marginBottom: 14,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 200, flex: '1 1 200px' }}>
        <UserOutlined style={{ color: token.colorPrimary, fontSize: 16 }} />
        <div>
          <Text strong style={{ display: 'block', lineHeight: 1.2 }}>{nome}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {customer.codice_fiscale && <>CF: {customer.codice_fiscale} · </>}
            {customer.cellulare ?? customer.telefono ?? '—'}
          </Text>
        </div>
      </div>
      {customer.email && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <MailOutlined style={{ color: token.colorTextSecondary, fontSize: 13 }} />
          <Text style={{ fontSize: 12 }}>{customer.email}</Text>
        </div>
      )}
      {customer.indirizzo && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <EnvironmentOutlined style={{ color: token.colorTextSecondary, fontSize: 13 }} />
          <Text style={{ fontSize: 12 }}>
            {customer.indirizzo} {customer.numero_civico ?? ''}, {customer.citta ?? ''} {customer.provincia ? `(${customer.provincia})` : ''}
          </Text>
        </div>
      )}
      {vehicle && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
          <CarOutlined style={{ color: token.colorPrimary, fontSize: 16 }} />
          <div>
            <Text strong style={{ display: 'block', lineHeight: 1.2 }}>{vehicle.targa}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>{vehicle.marca} {vehicle.modello} {vehicle.anno}</Text>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Pagina principale ────────────────────────────────────────────────────────

export default function ConsegnaAdcPage() {
  const { setTitle } = usePageTitle()
  useEffect(() => { setTitle('Consegna Auto di Cortesia') }, [setTitle])

  const { token } = theme.useToken()
  const [messageApi, contextHolder] = message.useMessage()
  const queryClient = useQueryClient()

  const [activeTab, setActiveTab] = useState('consegna')
  const [selectedIdA, setSelectedIdA] = useState<number | null>(null)
  const [selectedIdB, setSelectedIdB] = useState<number | null>(null)
  const [formA, setFormA] = useState<Record<string, unknown>>({})
  const [formB, setFormB] = useState<Record<string, unknown>>({})
  const [emailModal, setEmailModal] = useState<{ open: boolean; tipo: 'consegna' | 'riconsegna'; email: string } | null>(null)
  const [viewA, setViewA] = useState<'list' | 'form'>('list')
  const [viewB, setViewB] = useState<'list' | 'form'>('list')
  // ── Queries ───────────────────────────────────────────────────────────────

  const listA = useQuery({
    queryKey: ['adc-da-consegnare'],
    queryFn: async () => (await axiosInstance.get<AssignmentListItem[]>(`${BASE_URL}/da-consegnare`)).data,
  })
  const listB = useQuery({
    queryKey: ['adc-da-riconsegnare'],
    queryFn: async () => (await axiosInstance.get<AssignmentListItem[]>(`${BASE_URL}/da-riconsegnare`)).data,
  })
  const listStorico = useQuery({
    queryKey: ['adc-storico'],
    queryFn: async () => (await axiosInstance.get<AssignmentStorico[]>(`${BASE_URL}/storico`)).data,
  })
  const detailA = useQuery({
    queryKey: ['adc-detail', selectedIdA],
    queryFn: async () => (await axiosInstance.get<AssignmentDetail>(`${BASE_URL}/${selectedIdA}/detail`)).data,
    enabled: !!selectedIdA,
  })
  const detailB = useQuery({
    queryKey: ['adc-detail', selectedIdB],
    queryFn: async () => (await axiosInstance.get<AssignmentDetail>(`${BASE_URL}/${selectedIdB}/detail`)).data,
    enabled: !!selectedIdB,
  })

  useEffect(() => {
    if (!detailB.data) return
    const d = detailB.data
    setFormB({
      km_fine: d.km_fine, livello_carburante_fine: d.livello_carburante_fine,
      dichiarazione_no_incidenti: d.dichiarazione_no_incidenti ?? true,
      note_incidenti: d.note_incidenti, firma_responsabile_b64: d.firma_responsabile_b64,
    })
  }, [detailB.data])

  // ── Mutations ─────────────────────────────────────────────────────────────

  const mutConsegna = useMutation({
    mutationFn: async () => axiosInstance.post(`${BASE_URL}/${selectedIdA}/consegna`, {
      ...formA,
      scadenza_patente: formA.scadenza_patente ? (formA.scadenza_patente as dayjs.Dayjs).format('YYYY-MM-DD') : undefined,
      data_consegna_effettiva: new Date().toISOString(),
    }),
    onSuccess: () => {
      messageApi.success('Consegna registrata.')
      queryClient.invalidateQueries({ queryKey: ['adc-da-consegnare'] })
      queryClient.invalidateQueries({ queryKey: ['adc-da-riconsegnare'] })
      queryClient.invalidateQueries({ queryKey: ['adc-storico'] })
      queryClient.invalidateQueries({ queryKey: ['adc-detail', selectedIdA] })
      setSelectedIdA(null); setFormA({}); setViewA('list')
    },
    onError: (e: unknown) => {
      messageApi.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Errore consegna.')
    },
  })

  const mutRiconsegna = useMutation({
    mutationFn: async () => axiosInstance.post(`${BASE_URL}/${selectedIdB}/riconsegna`, {
      ...formB, data_riconsegna_effettiva: new Date().toISOString(),
    }),
    onSuccess: () => {
      messageApi.success('Riconsegna registrata.')
      queryClient.invalidateQueries({ queryKey: ['adc-da-riconsegnare'] })
      queryClient.invalidateQueries({ queryKey: ['adc-storico'] })
      queryClient.invalidateQueries({ queryKey: ['adc-detail', selectedIdB] })
      setSelectedIdB(null); setFormB({}); setViewB('list')
    },
    onError: (e: unknown) => {
      messageApi.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Errore riconsegna.')
    },
  })

  const mutFotoPatente = useMutation({
    mutationFn: async ({ tipo, file }: { tipo: 'fronte' | 'retro'; file: File }) => {
      const fd = new FormData(); fd.append('file', file)
      return axiosInstance.post(`${BASE_URL}/${selectedIdA}/foto-patente/${tipo}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['adc-detail', selectedIdA] }),
    onError: () => messageApi.error('Errore upload foto patente.'),
  })

  const mutDelFotoPatente = useMutation({
    mutationFn: async (tipo: 'fronte' | 'retro') =>
      axiosInstance.delete(`${BASE_URL}/${selectedIdA}/foto-patente/${tipo}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['adc-detail', selectedIdA] }),
    onError: () => messageApi.error('Errore eliminazione foto patente.'),
  })

  const mutFotoDanni = useMutation({
    mutationFn: async ({ fase, file, assignmentId }: { fase: string; file: File; assignmentId: number }) => {
      const fd = new FormData(); fd.append('file', file)
      return axiosInstance.post(`${BASE_URL}/${assignmentId}/foto-danni/${fase}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
    },
    onSuccess: (_d, vars) => queryClient.invalidateQueries({ queryKey: ['adc-detail', vars.assignmentId] }),
    onError: () => messageApi.error('Errore upload foto danni.'),
  })

  const mutDelFoto = useMutation({
    mutationFn: async ({ fase, filename, assignmentId }: { fase: string; filename: string; assignmentId: number }) =>
      axiosInstance.delete(`${BASE_URL}/${assignmentId}/foto-danni/${fase}/${filename}`),
    onSuccess: (_d, vars) => queryClient.invalidateQueries({ queryKey: ['adc-detail', vars.assignmentId] }),
    onError: () => messageApi.error('Errore eliminazione foto.'),
  })

  const mutInviaPdf = useMutation({
    mutationFn: async ({ tipo, email }: { tipo: 'consegna' | 'riconsegna'; email: string }) => {
      const id = tipo === 'consegna' ? selectedIdA : selectedIdB
      return axiosInstance.post(`${BASE_URL}/${id}/invia-pdf`, { tipo, email })
    },
    onSuccess: () => { messageApi.success('PDF inviato.'); setEmailModal(null) },
    onError: (e: unknown) => {
      messageApi.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Errore invio email.')
    },
  })

  const downloadPdf = async (tipo: 'consegna' | 'riconsegna') => {
    const id = tipo === 'consegna' ? selectedIdA : selectedIdB
    if (!id) return
    try {
      const res = await axiosInstance.get(`${BASE_URL}/${id}/pdf/${tipo}`, { responseType: 'blob' })
      const url = URL.createObjectURL(res.data)
      const a = document.createElement('a'); a.href = url; a.download = `adc_${tipo}_${id}.pdf`; a.click()
      URL.revokeObjectURL(url)
    } catch { messageApi.error('Errore generazione PDF.') }
  }

  const downloadPdfById = async (id: number, tipo: 'consegna' | 'riconsegna') => {
    try {
      const res = await axiosInstance.get(`${BASE_URL}/${id}/pdf/${tipo}`, { responseType: 'blob' })
      const url = URL.createObjectURL(res.data)
      const a = document.createElement('a'); a.href = url; a.download = `adc_${tipo}_${id}.pdf`; a.click()
      URL.revokeObjectURL(url)
    } catch { messageApi.error('Errore generazione PDF.') }
  }

  const canConfirmA = () => !!(formA.firma_cliente_b64 && formA.consenso_gdpr && formA.livello_carburante_inizio)
  const canConfirmB = () => !!(formB.firma_responsabile_b64 && formB.livello_carburante_fine)

  // ── Stili condivisi ───────────────────────────────────────────────────────

  const sectionCard: React.CSSProperties = {
    background: token.colorBgContainer,
    border: `1px solid ${token.colorBorderSecondary}`,
    borderRadius: token.borderRadius,
    padding: '12px 14px',
    marginBottom: 10,
  }

  // ── Tab A: Consegna ───────────────────────────────────────────────────────

  const consegnaColumns: ColumnsType<AssignmentListItem> = [
    {
      title: 'Targa',
      key: 'targa',
      width: 100,
      render: (_: unknown, r: AssignmentListItem) => <Text strong>{r.vehicle?.targa ?? '—'}</Text>,
    },
    {
      title: 'Marca / Modello',
      key: 'modello',
      render: (_: unknown, r: AssignmentListItem) =>
        r.vehicle ? `${r.vehicle.marca ?? ''} ${r.vehicle.modello ?? ''}`.trim() || '—' : '—',
    },
    {
      title: 'Cliente',
      key: 'cliente',
      ellipsis: true,
      render: (_: unknown, r: AssignmentListItem) => {
        const c = r.customer
        if (!c) return '—'
        return c.tipo === 'azienda' ? c.ragione_sociale ?? '—' : `${c.nome ?? ''} ${c.cognome ?? ''}`.trim() || '—'
      },
    },
    {
      title: 'Da',
      dataIndex: 'data_inizio',
      width: 110,
      render: (v: string) => dayjs(v).format('DD/MM/YYYY'),
    },
    {
      title: 'A',
      dataIndex: 'data_fine_prevista',
      width: 110,
      render: (v: string) => dayjs(v).format('DD/MM/YYYY'),
    },
  ]

  const tabConsegna = (
    <div>
      {viewA === 'list' && (
        <>
          {listA.isLoading && <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>}
          {listA.isError && <Alert type="error" message="Errore caricamento assegnazioni." style={{ marginBottom: 12 }} />}
          {!listA.isLoading && (
            <Table<AssignmentListItem>
              dataSource={listA.data ?? []}
              columns={consegnaColumns}
              rowKey="id"
              size="small"
              pagination={{ pageSize: 20, showSizeChanger: false, hideOnSinglePage: true }}
              locale={{ emptyText: 'Nessuna assegnazione da consegnare.' }}
              onRow={record => ({ onClick: () => { setSelectedIdA(record.id); setFormA({}); setViewA('form') }, style: { cursor: 'pointer' } })}
            />
          )}
        </>
      )}

      {viewA === 'form' && (
        <div>
          <Button
            icon={<ArrowLeftOutlined />}
            style={{ marginBottom: 12 }}
            onClick={() => { setSelectedIdA(null); setFormA({}); setViewA('list') }}
          >
            Torna alla lista
          </Button>

          {selectedIdA && detailA.isLoading && <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>}

          {selectedIdA && detailA.data && (() => {
        const d = detailA.data
        const ro = d.stato !== 'PRENOTATA'
        return (
          <>
            {ro && <Alert type="info" showIcon message="Stato non PRENOTATA — sola lettura." style={{ marginBottom: 10 }} />}

            {/* Barra cliente + veicolo */}
            {d.customer && <CustomerBar customer={d.customer} vehicle={d.vehicle} />}

            {/* ── Patente ── */}
            <div style={sectionCard}>
              <SectionLabel><IdcardOutlined style={{ marginRight: 4 }} />Patente conducente</SectionLabel>
              <Row gutter={[8, 8]}>
                <Col xs={24} sm={8} md={6}>
                  <FieldLabel>Numero patente</FieldLabel>
                  <Input value={formA.numero_patente as string ?? ''} disabled={ro}
                    onChange={e => setFormA(p => ({ ...p, numero_patente: e.target.value }))}
                    placeholder="MI 1234567A" autoComplete="one-time-code" />
                </Col>
                <Col xs={8} sm={3} md={2}>
                  <FieldLabel>Cat.</FieldLabel>
                  <Input value={formA.categoria_patente as string ?? ''} disabled={ro}
                    onChange={e => setFormA(p => ({ ...p, categoria_patente: e.target.value }))}
                    placeholder="B" autoComplete="one-time-code" />
                </Col>
                <Col xs={16} sm={7} md={7}>
                  <FieldLabel>Ente di rilascio</FieldLabel>
                  <Input value={formA.ente_rilascio_patente as string ?? ''} disabled={ro}
                    onChange={e => setFormA(p => ({ ...p, ente_rilascio_patente: e.target.value }))}
                    placeholder="Motorizzazione..." autoComplete="one-time-code" />
                </Col>
                <Col xs={12} sm={6} md={4}>
                  <FieldLabel>Scadenza</FieldLabel>
                  <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
                    value={formA.scadenza_patente as dayjs.Dayjs | undefined}
                    onChange={v => setFormA(p => ({ ...p, scadenza_patente: v }))} disabled={ro} />
                </Col>
                <Col xs={12} sm={24} md={5}>
                  <FieldLabel>Foto patente</FieldLabel>
                  {/* Image.PreviewGroup: fronte e retro navigabili insieme nel visualizzatore */}
                  <Image.PreviewGroup>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <PhotoSlot url={d.foto_patente_fronte_url} label="Fronte"
                        onUpload={f => mutFotoPatente.mutate({ tipo: 'fronte', file: f })}
                        onDelete={() => mutDelFotoPatente.mutate('fronte')}
                        disabled={ro} />
                      <PhotoSlot url={d.foto_patente_retro_url} label="Retro"
                        onUpload={f => mutFotoPatente.mutate({ tipo: 'retro', file: f })}
                        onDelete={() => mutDelFotoPatente.mutate('retro')}
                        disabled={ro} />
                    </div>
                  </Image.PreviewGroup>
                  {/* Avviso scadenza patente */}
                  {(() => {
                    const sp = formA.scadenza_patente as dayjs.Dayjs | undefined
                    return sp && dayjs().isAfter(sp) ? (
                      <Alert type="warning" showIcon
                        message={`Patente scaduta il ${sp.format('DD/MM/YYYY')}`}
                        style={{ marginTop: 8 }} />
                    ) : null
                  })()}
                </Col>
              </Row>
            </div>

            {/* ── Consegna ── */}
            <div style={sectionCard}>
              <SectionLabel><CarOutlined style={{ marginRight: 4 }} />Condizioni consegna</SectionLabel>
              <Row gutter={[8, 8]}>
                <Col xs={24} sm={8} md={6}>
                  <FieldLabel>Motivo richiesta</FieldLabel>
                  <Select style={{ width: '100%' }} value={formA.motivo_richiesta as string ?? undefined}
                    onChange={v => setFormA(p => ({ ...p, motivo_richiesta: v }))} disabled={ro}
                    placeholder="Seleziona..." options={MOTIVI} />
                </Col>
                <Col xs={8} sm={4} md={3}>
                  <FieldLabel>Km iniziali</FieldLabel>
                  <Input type="number" suffix="km" disabled={ro}
                    value={formA.km_inizio as number ?? ''}
                    onChange={e => { const v = e.target.value ? parseInt(e.target.value) : undefined; setFormA(p => ({ ...p, km_inizio: v })) }} />
                </Col>
                <Col xs={16} sm={12} md={15}>
                  <FieldLabel required>Carburante</FieldLabel>
                  <FuelPicker value={formA.livello_carburante_inizio as string}
                    onChange={v => setFormA(p => ({ ...p, livello_carburante_inizio: v }))} disabled={ro} />
                </Col>
              </Row>
              <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                <Col xs={24} sm={12}>
                  <FieldLabel>Accessori inclusi</FieldLabel>
                  <TextArea rows={2} disabled={ro} placeholder="Spare key, cavi, triangolo..."
                    value={formA.accessori_inclusi as string ?? ''}
                    onChange={e => setFormA(p => ({ ...p, accessori_inclusi: e.target.value }))} />
                </Col>
                <Col xs={24} sm={12}>
                  <FieldLabel>Danni preesistenti</FieldLabel>
                  <TextArea rows={2} disabled={ro} placeholder="Descrivere eventuali danni..."
                    value={formA.danni_preesistenti as string ?? ''}
                    onChange={e => setFormA(p => ({ ...p, danni_preesistenti: e.target.value }))} />
                </Col>
              </Row>
              <div style={{ marginTop: 8 }}>
                <FieldLabel>Foto danni</FieldLabel>
                <PhotoGrid items={d.foto_danni_consegna ?? []} disabled={ro}
                  onAdd={f => mutFotoDanni.mutate({ fase: 'consegna', file: f, assignmentId: d.id })}
                  onDelete={fn => mutDelFoto.mutate({ fase: 'consegna', filename: fn, assignmentId: d.id })} />
              </div>
            </div>

            {/* ── Firma e consenso ── */}
            <div style={sectionCard}>
              <SectionLabel>Consenso e firma</SectionLabel>
              <Row gutter={[12, 8]} align="middle">
                <Col xs={24} md={14}>
                  <SignatureCanvas label="Firma cliente *"
                    value={d.firma_cliente_b64}
                    onChange={v => setFormA(p => ({ ...p, firma_cliente_b64: v }))} disabled={ro} />
                </Col>
                <Col xs={24} md={10}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <Checkbox
                      checked={formA.consenso_gdpr as boolean ?? false} disabled={ro}
                      onChange={e => setFormA(p => ({ ...p, consenso_gdpr: e.target.checked }))}
                      style={{ fontSize: 13, lineHeight: 1.4 }}
                    >
                      Il cliente dichiara di aver letto e accettato le clausole contrattuali e presta il consenso al trattamento dei dati personali secondo quanto indicato nell'informativa privacy. <span style={{ color: token.colorError }}>*</span>
                    </Checkbox>
                    {!ro && (
                      <>
                        <Button type="primary" size="large" block icon={<CheckCircleOutlined />}
                          loading={mutConsegna.isPending} disabled={!canConfirmA()}
                          onClick={() => mutConsegna.mutate()}
                          style={{ height: 48 }}
                        >
                          Conferma consegna
                        </Button>
                        {!canConfirmA() && (
                          <div style={{ fontSize: 12, color: token.colorTextSecondary }}>
                            Obbligatori: carburante, firma e GDPR
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </Col>
              </Row>
            </div>

            {/* Azioni post-consegna */}
            {ro && d.data_consegna_effettiva && (
              <div style={{ ...sectionCard, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{
                  width: 8, height: 8, borderRadius: '50%', background: token.colorSuccess, flexShrink: 0,
                }} />
                <Text style={{ flex: 1, fontSize: 13 }}>
                  Consegnata il {dayjs(d.data_consegna_effettiva).format('DD/MM/YYYY HH:mm')}
                </Text>
                <Button icon={<DownloadOutlined />} onClick={() => downloadPdf('consegna')}>PDF</Button>
                <Button icon={<MailOutlined />}
                  onClick={() => setEmailModal({ open: true, tipo: 'consegna', email: d.customer?.email ?? '' })}>
                  Email
                </Button>
              </div>
            )}
          </>
        )
      })()}
        </div>
      )}
    </div>
  )

  // ── Tab B: Riconsegna ─────────────────────────────────────────────────────

  const riconsegnaColumns: ColumnsType<AssignmentListItem> = [
    {
      title: 'Targa',
      key: 'targa',
      width: 100,
      render: (_: unknown, r: AssignmentListItem) => <Text strong>{r.vehicle?.targa ?? '—'}</Text>,
    },
    {
      title: 'Marca / Modello',
      key: 'modello',
      render: (_: unknown, r: AssignmentListItem) =>
        r.vehicle ? `${r.vehicle.marca ?? ''} ${r.vehicle.modello ?? ''}`.trim() || '—' : '—',
    },
    {
      title: 'Cliente',
      key: 'cliente',
      ellipsis: true,
      render: (_: unknown, r: AssignmentListItem) => {
        const c = r.customer
        if (!c) return '—'
        return c.tipo === 'azienda' ? c.ragione_sociale ?? '—' : `${c.nome ?? ''} ${c.cognome ?? ''}`.trim() || '—'
      },
    },
    {
      title: 'Da',
      dataIndex: 'data_inizio',
      width: 110,
      render: (v: string) => dayjs(v).format('DD/MM/YYYY'),
    },
    {
      title: 'A',
      dataIndex: 'data_fine_prevista',
      width: 110,
      render: (v: string) => dayjs(v).format('DD/MM/YYYY'),
    },
    {
      title: 'Data consegna',
      dataIndex: 'data_consegna_effettiva',
      width: 130,
      render: (v?: string) => v ? dayjs(v).format('DD/MM/YYYY HH:mm') : <Text type="secondary">—</Text>,
    },
  ]

  const tabRiconsegna = (
    <div>
      {viewB === 'list' && (
        <>
          {listB.isLoading && <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>}
          {listB.isError && <Alert type="error" message="Errore caricamento assegnazioni." style={{ marginBottom: 12 }} />}
          {!listB.isLoading && (
            <Table<AssignmentListItem>
              dataSource={listB.data ?? []}
              columns={riconsegnaColumns}
              rowKey="id"
              size="small"
              pagination={{ pageSize: 20, showSizeChanger: false, hideOnSinglePage: true }}
              locale={{ emptyText: 'Nessuna assegnazione in corso da riconsegnare.' }}
              onRow={record => ({ onClick: () => { setSelectedIdB(record.id); setFormB({}); setViewB('form') }, style: { cursor: 'pointer' } })}
            />
          )}
        </>
      )}

      {viewB === 'form' && (
        <div>
          <Button
            icon={<ArrowLeftOutlined />}
            style={{ marginBottom: 12 }}
            onClick={() => { setSelectedIdB(null); setFormB({}); setViewB('list') }}
          >
            Torna alla lista
          </Button>

          {selectedIdB && detailB.isLoading && <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>}

          {selectedIdB && detailB.data && (() => {
        const d = detailB.data
        const ro = d.stato !== 'IN_CORSO'
        return (
          <>
            {ro && <Alert type="info" showIcon message="Stato non IN CORSO — sola lettura." style={{ marginBottom: 10 }} />}

            {/* Riepilogo consegna originale */}
            <div style={{
              ...sectionCard, marginBottom: 10,
              display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center',
            }}>
              {d.vehicle && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <CarOutlined style={{ color: token.colorPrimary }} />
                  <Text strong>{d.vehicle.targa}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>{d.vehicle.marca} {d.vehicle.modello}</Text>
                </div>
              )}
              {d.customer && (() => {
                const nome = d.customer.tipo === 'azienda'
                  ? d.customer.ragione_sociale
                  : `${d.customer.nome ?? ''} ${d.customer.cognome ?? ''}`.trim()
                return <Text style={{ fontSize: 13 }}>{nome}</Text>
              })()}
              <div style={{ display: 'flex', gap: 16, marginLeft: 'auto', flexWrap: 'wrap' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 11 }}>CONSEGNATA</Text>
                  <Text style={{ display: 'block', fontSize: 12, fontWeight: 600 }}>
                    {d.data_consegna_effettiva ? dayjs(d.data_consegna_effettiva).format('DD/MM/YYYY HH:mm') : '—'}
                  </Text>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 11 }}>KM INIZIO</Text>
                  <Text style={{ display: 'block', fontSize: 12, fontWeight: 600 }}>{d.km_inizio ?? '—'}</Text>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 11 }}>CARBURANTE</Text>
                  <Text style={{ display: 'block', fontSize: 12, fontWeight: 600 }}>{d.livello_carburante_inizio ?? '—'}</Text>
                </div>
              </div>
            </div>

            {/* ── Riconsegna ── */}
            <div style={sectionCard}>
              <SectionLabel>Condizioni riconsegna</SectionLabel>
              <Row gutter={[8, 8]}>
                <Col xs={10} sm={5} md={4}>
                  <FieldLabel>Km finali</FieldLabel>
                  <Input type="number" suffix="km" disabled={ro}
                    value={formB.km_fine as number ?? ''}
                    onChange={e => setFormB(p => ({ ...p, km_fine: e.target.value ? parseInt(e.target.value) : undefined }))} />
                </Col>
                <Col xs={14} sm={19} md={20}>
                  <FieldLabel required>Carburante</FieldLabel>
                  <FuelPicker value={formB.livello_carburante_fine as string}
                    onChange={v => setFormB(p => ({ ...p, livello_carburante_fine: v }))} disabled={ro} />
                </Col>
              </Row>
              <div style={{ marginTop: 8 }}>
                <FieldLabel>Foto danni riconsegna</FieldLabel>
                <PhotoGrid items={d.foto_danni_riconsegna ?? []} disabled={ro}
                  onAdd={f => mutFotoDanni.mutate({ fase: 'riconsegna', file: f, assignmentId: d.id })}
                  onDelete={fn => mutDelFoto.mutate({ fase: 'riconsegna', filename: fn, assignmentId: d.id })} />
              </div>
            </div>

            {/* ── Sinistri ── */}
            <div style={sectionCard}>
              <SectionLabel>Dichiarazione sinistri</SectionLabel>
              <Checkbox
                checked={formB.dichiarazione_no_incidenti as boolean ?? true} disabled={ro}
                onChange={e => setFormB(p => ({ ...p, dichiarazione_no_incidenti: e.target.checked, note_incidenti: e.target.checked ? undefined : p.note_incidenti }))}
                style={{ fontSize: 13 }}
              >
                Nessun incidente durante il periodo di utilizzo
              </Checkbox>
              {!(formB.dichiarazione_no_incidenti ?? true) && (
                <TextArea rows={3} style={{ marginTop: 8 }} disabled={ro}
                  placeholder="Data, luogo, dinamica, danni..."
                  value={formB.note_incidenti as string ?? ''}
                  onChange={e => setFormB(p => ({ ...p, note_incidenti: e.target.value }))} />
              )}
            </div>

            {/* ── Firma ── */}
            <div style={sectionCard}>
              <SectionLabel>Firma responsabile</SectionLabel>
              <Row gutter={[12, 8]} align="middle">
                <Col xs={24} md={14}>
                  <SignatureCanvas label="Firma responsabile *"
                    value={d.firma_responsabile_b64}
                    onChange={v => setFormB(p => ({ ...p, firma_responsabile_b64: v }))} disabled={ro} />
                </Col>
                <Col xs={24} md={10}>
                  {!ro && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <Button type="primary" size="large" block icon={<CheckCircleOutlined />}
                        loading={mutRiconsegna.isPending} disabled={!canConfirmB()}
                        onClick={() => mutRiconsegna.mutate()} style={{ height: 48 }}
                      >
                        Conferma riconsegna
                      </Button>
                      {!canConfirmB() && (
                        <div style={{ fontSize: 12, color: token.colorTextSecondary }}>
                          Obbligatori: carburante e firma responsabile
                        </div>
                      )}
                    </div>
                  )}
                </Col>
              </Row>
            </div>

            {ro && d.data_riconsegna_effettiva && (
              <div style={{ ...sectionCard, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: token.colorSuccess, flexShrink: 0 }} />
                <Text style={{ flex: 1, fontSize: 13 }}>
                  Riconsegnata il {dayjs(d.data_riconsegna_effettiva).format('DD/MM/YYYY HH:mm')}
                </Text>
                <Button icon={<DownloadOutlined />} onClick={() => downloadPdf('riconsegna')}>PDF</Button>
                <Button icon={<MailOutlined />}
                  onClick={() => setEmailModal({ open: true, tipo: 'riconsegna', email: d.customer?.email ?? '' })}>
                  Email
                </Button>
              </div>
            )}
          </>
        )
      })()}
        </div>
      )}
    </div>
  )

  // ── Tab C: Storico consegne ───────────────────────────────────────────────

  const storicoColumns: ColumnsType<AssignmentStorico> = [
    {
      title: 'Targa',
      dataIndex: ['vehicle', 'targa'],
      width: 100,
      render: (v: string) => <Text strong>{v ?? '—'}</Text>,
    },
    {
      title: 'Modello',
      key: 'modello',
      width: 160,
      render: (_: unknown, r: AssignmentStorico) =>
        r.vehicle ? `${r.vehicle.marca ?? ''} ${r.vehicle.modello ?? ''}`.trim() || '—' : '—',
    },
    {
      title: 'Cliente',
      key: 'cliente',
      ellipsis: true,
      render: (_: unknown, r: AssignmentStorico) => {
        const c = r.customer
        if (!c) return '—'
        return c.tipo === 'azienda' ? c.ragione_sociale ?? '—' : `${c.nome ?? ''} ${c.cognome ?? ''}`.trim() || '—'
      },
    },
    {
      title: 'Da',
      dataIndex: 'data_inizio',
      width: 110,
      render: (v: string) => dayjs(v).format('DD/MM/YYYY'),
    },
    {
      title: 'A',
      dataIndex: 'data_fine_prevista',
      width: 110,
      render: (v: string) => dayjs(v).format('DD/MM/YYYY'),
    },
    {
      title: 'Data consegna',
      dataIndex: 'data_consegna_effettiva',
      width: 130,
      render: (v?: string) => v ? dayjs(v).format('DD/MM/YYYY HH:mm') : <Text type="secondary">—</Text>,
    },
    {
      title: 'Data riconsegna',
      dataIndex: 'data_riconsegna_effettiva',
      width: 140,
      render: (v?: string) => v
        ? dayjs(v).format('DD/MM/YYYY HH:mm')
        : <Tag color="orange">In corso</Tag>,
    },
    {
      title: 'PDF',
      key: 'pdf',
      width: 200,
      render: (_: unknown, r: AssignmentStorico) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <Button size="small" icon={<DownloadOutlined />}
            onClick={() => downloadPdfById(r.id, 'consegna')}>
            Consegna
          </Button>
          {r.data_riconsegna_effettiva && (
            <Button size="small" icon={<DownloadOutlined />}
              onClick={() => downloadPdfById(r.id, 'riconsegna')}>
              Riconsegna
            </Button>
          )}
        </div>
      ),
    },
  ]

  const tabStorico = (
    <div>
      {listStorico.isLoading && <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>}
      {listStorico.isError && <Alert type="error" message="Errore caricamento storico." style={{ marginBottom: 12 }} />}
      {!listStorico.isLoading && (
        <Table<AssignmentStorico>
          dataSource={listStorico.data ?? []}
          columns={storicoColumns}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 20, showSizeChanger: false, hideOnSinglePage: true }}
          locale={{ emptyText: 'Nessuna assegnazione in corso o completata.' }}
          scroll={{ x: 960 }}
        />
      )}
    </div>
  )

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '12px 16px 32px', maxWidth: 1100, margin: '0 auto' }}>
      {contextHolder}

      <Tabs
        activeKey={activeTab} onChange={setActiveTab}
        size="middle" type="card"
        style={{ '--ant-tabs-card-padding': '8px 16px' } as React.CSSProperties}
        items={[
          { key: 'consegna',   label: '↓ Consegna',   children: <div style={{ paddingTop: 12 }}>{tabConsegna}</div> },
          { key: 'riconsegna', label: '↑ Riconsegna', children: <div style={{ paddingTop: 12 }}>{tabRiconsegna}</div> },
          { key: 'storico',    label: <span><HistoryOutlined /> In corso / Storico</span>, children: <div style={{ paddingTop: 12 }}>{tabStorico}</div> },
        ]}
      />

      <Modal
        open={!!emailModal?.open}
        title={`Invia PDF ${emailModal?.tipo === 'consegna' ? 'consegna' : 'riconsegna'}`}
        onCancel={() => setEmailModal(null)}
        footer={[
          <Button key="cancel" onClick={() => setEmailModal(null)}>Annulla</Button>,
          <Button key="send" type="primary" icon={<MailOutlined />}
            loading={mutInviaPdf.isPending} disabled={!emailModal?.email}
            onClick={() => emailModal && mutInviaPdf.mutate({ tipo: emailModal.tipo, email: emailModal.email })}>
            Invia
          </Button>,
        ]}
      >
        <div style={{ marginTop: 8 }}>
          <FieldLabel>Indirizzo email destinatario</FieldLabel>
          <Input size="large" type="email" value={emailModal?.email ?? ''}
            onChange={e => setEmailModal(p => p ? { ...p, email: e.target.value } : null)}
            placeholder="cliente@email.com" />
        </div>
      </Modal>
    </div>
  )
}
